import { Theme } from '@mos-cat/ds-theme';
import { themeGet } from '@styled-system/theme-get';
import { useTheme } from '@emotion/react';
import { Ref } from 'react';
import { ComponentWithAs, forwardRefWithAs, PropsWithAs } from '../as';
import { Box, BoxProps } from '../Box/Box';
import { cx, ecss, efacepaint } from '../reexports/emotion';
import { ThemeType } from '@mos-cat/ds-theme/temp/types/theme';

function calculateSpacing(
  paddingToken: any,
  borderWidth: any,
  props: BoxProps & { theme: Theme }
) {
  const paddingScale = themeGet(`space.${paddingToken}`, null)(props);
  if (paddingScale === null) return undefined;
  if (paddingScale === 0) return '0rem';
  return `calc(${paddingScale} - ${borderWidth})`;
}

function getValue(
  paddingToken: any,
  borderToken: any,
  props: BoxProps & { theme: Theme }
) {
  const isArrayBorder = Array.isArray(borderToken);
  const isArrayPadding = Array.isArray(paddingToken);
  const borderWidth = isArrayBorder
    ? borderToken.map((border: any) =>
        themeGet(`borderWidths.${border}`, 0)(props)
      )
    : themeGet(`borderWidths.${borderToken}`, '0rem')(props);
  if (isArrayPadding && isArrayBorder) {
    let currentPadding = '0rem';
    let currentBorder = '0rem';
    return paddingToken.map((padding: any, index: number) => {
      if (padding) currentPadding = padding;
      if (borderWidth[index]) currentBorder = borderWidth[index];
      return calculateSpacing(currentPadding, currentBorder, props);
    });
  } else if (isArrayPadding) {
    let currentPadding = '0rem';
    return paddingToken.map((padding: any) => {
      if (padding) currentPadding = padding;
      return calculateSpacing(currentPadding, borderWidth, props);
    });
  } else if (isArrayBorder) {
    let currentBorder = '0rem';
    return borderWidth.map((border: any) => {
      if (border) currentBorder = border;
      return calculateSpacing(paddingToken, currentBorder, props);
    });
  }
  return calculateSpacing(paddingToken, borderWidth, props);
}

function mergeTwoArrays(arg1: any, arg2: any) {
  if (Array.isArray(arg1) && Array.isArray(arg2)) {
    const result = [];
    for (let i = 0; i < Math.max(arg1.length, arg2.length); i++)
      result.push(arg1[i] || arg2[i]);
    return result;
  }
  return arg1 || arg2;
}

function styles(props: BoxProps & { theme: Theme }) {
  const mq = efacepaint(
    themeGet(
      'breakpoints',
      []
    )(props).map((bp: string) => `@media (min-width: ${bp})`)
  );
  const border = props.border || props.borderWidth;
  const px = getValue(props.px || props.paddingX, border, props);
  const py = getValue(props.py || props.paddingY, border, props);
  const style = mq({
    padding: getValue(props.p || props.padding, border, props),
    paddingTop: mergeTwoArrays(
      getValue(props.pt || props.paddingTop, border, props),
      py
    ),
    paddingBottom: mergeTwoArrays(
      getValue(props.pb || props.paddingBottom, border, props),
      py
    ),
    paddingRight: mergeTwoArrays(
      getValue(props.pr || props.paddingRight, border, props),
      px
    ),
    paddingLeft: mergeTwoArrays(
      getValue(props.pl || props.paddingLeft, border, props),
      px
    ),
  });
  return style;
}

function useClassNames(props: BoxProps) {
  let theme: ThemeType = useTheme();
  return ecss(styles({ ...props, theme }), 'label:BoxWithBorderCompensation');
}

function omitPaddingProps(props: BoxProps) {
  let {
    p,
    padding,
    px,
    paddingX,
    py,
    paddingY,
    pt,
    paddingTop,
    pb,
    paddingBottom,
    pl,
    paddingLeft,
    pr,
    paddingRight,
    ...rest
  } = props;
  return rest;
}

function BoxComponent(
  { className, ...props }: PropsWithAs<BoxProps, 'div'>,
  ref: Ref<HTMLDivElement>
) {
  const boxCN = useClassNames(props);
  const componentProps = omitPaddingProps(props);
  return <Box {...componentProps} className={cx(boxCN, className)} ref={ref} />;
}

/**
 * @deprecated
 */
export const BoxWithBorderCompensation: ComponentWithAs<
  BoxProps,
  'div'
> = forwardRefWithAs<BoxProps, 'div'>(BoxComponent);
